import { useState } from "react";
import { ChevronDown, X } from "lucide-react";
import type { ExperienceItem } from "@/lib/types";
import { MAX_COMPARISON_ITEMS } from "./comparisonDecision";
import { FOCUS_RING } from "./interactionStyles";

export interface TrayItem {
  exp:          ExperienceItem;
  neighborhood: string;
  compositeId:  string;
}

// Re-exported so existing callers (experiences.tsx) can keep importing the
// limit from the tray without also reaching into comparisonDecision.ts.
export { MAX_COMPARISON_ITEMS };

// ─── Comparar — right-rail tray ─────────────────────────────────────────────
// Marketplace Comparison V1 — this was "Tu selección" building a route to
// send to the Planner (removed entirely: no Planner integration, no
// persistence, no auth in this feature). It's now a comparison staging
// area — up to MAX_COMPARISON_ITEMS items, opened here into a real
// side-by-side comparison (ComparisonDrawer). Session-only, never touches
// the backend, no drag-and-drop reordering (comparison order doesn't need
// one — the old reorder existed only to prep a Planner-bound sequence).
export function SelectionTray({
  items,
  onRemove,
  isModified,
  onReset,
  onOpenComparison,
  collapsible = false,
}: {
  items:            TrayItem[];
  onRemove:         (compositeId: string, title: string) => void;
  isModified:       boolean;
  onReset:          () => void;
  onOpenComparison: () => void;
  collapsible?:     boolean;
}) {
  const [open, setOpen] = useState(!collapsible);
  const hasItems = items.length > 0;
  const canCompare = items.length >= 2;

  return (
    <section
      className="rounded-xl overflow-hidden transition-colors duration-200"
      style={hasItems
        ? { border: "1px solid #E8194B61", background: "#FAFAFA" }
        : { border: "1px solid oklch(0.880 0.024 72 / 0.22)", background: "transparent" }}
    >
      <button
        type="button"
        onClick={() => collapsible && setOpen(v => !v)}
        aria-expanded={collapsible ? open : undefined}
        className={`w-full flex items-center justify-between gap-2 px-4 py-3 text-left ${FOCUS_RING}`}
        style={{
          cursor:       collapsible ? "pointer" : "default",
          borderBottom: open ? "1px solid oklch(0.880 0.024 72 / 0.20)" : "none",
          background:   "transparent",
          border:       "none",
          minHeight:    "44px",
        }}
      >
        <div>
          <p className="text-[9px] font-black tracking-[0.18em] uppercase" style={{ color: "#E8194B" }}>
            Comparar
          </p>
          <p className="text-[13px] font-bold mt-0.5" style={{ color: "oklch(0.120 0.040 258)" }}>
            {items.length} de {MAX_COMPARISON_ITEMS}
          </p>
        </div>
        {collapsible && (
          <ChevronDown
            size={14}
            aria-hidden
            className="transition-transform duration-200 motion-reduce:transition-none"
            style={{
              color:     "oklch(0.545 0.020 60 / 0.50)",
              transform: open ? "rotate(180deg)" : "none",
            }}
          />
        )}
      </button>

      {open && (
        <>
          {items.length === 0 ? (
            <div className="px-4 py-5">
              <p className="text-[12px] font-semibold text-center" style={{ color: "oklch(0.28 0.040 258 / 0.75)" }}>
                Aún no has añadido experiencias
              </p>
              <p className="text-[11px] leading-relaxed text-center mt-1" style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}>
                Añade hasta {MAX_COMPARISON_ITEMS} experiencias para comparar precio, duración, proveedor y disponibilidad.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-[oklch(0.880_0.024_72_/_0.16)]">
              {items.map(item => (
                <TrayRow
                  key={item.compositeId}
                  item={item}
                  onRemove={() => onRemove(item.compositeId, item.exp.title)}
                />
              ))}
            </div>
          )}

          {isModified && (
            <div className="px-4 py-2" style={{ borderTop: "1px solid oklch(0.880 0.024 72 / 0.15)" }}>
              <button
                type="button"
                onClick={onReset}
                className={`text-[10px] font-semibold transition-opacity hover:opacity-70 rounded-sm ${FOCUS_RING}`}
                style={{ color: "#5A5A5A", background: "none", border: "none", padding: 0, cursor: "pointer" }}
              >
                Vaciar comparación
              </button>
            </div>
          )}

          <div className="px-4 py-3" style={{ borderTop: "1px solid oklch(0.880 0.024 72 / 0.18)" }}>
            <button
              type="button"
              onClick={onOpenComparison}
              disabled={!canCompare}
              className={`w-full inline-flex items-center justify-center gap-1.5 text-[12px] font-bold px-4 py-2.5 rounded-lg transition-all duration-150 hover:opacity-90 active:scale-[0.98] disabled:cursor-not-allowed motion-reduce:transition-none motion-reduce:active:scale-100 ${FOCUS_RING}`}
              style={{
                minHeight:  "44px",
                color:      "oklch(0.998 0.003 72)",
                background: canCompare ? "oklch(0.120 0.040 258)" : "oklch(0.545 0.020 60 / 0.30)",
                opacity:    canCompare ? 1 : 0.6,
              }}
            >
              Ver comparación
            </button>
            {items.length === 1 && (
              <p className="mt-1.5 text-[10px] text-center leading-snug" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
                Añade al menos una experiencia más para comparar.
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function TrayRow({ item, onRemove }: { item: TrayItem; onRemove: () => void }) {
  const { exp, neighborhood } = item;
  const isProvider = exp.source_type === "provider";

  return (
    // Marketplace Interaction Polish V1, task 3 — a row added to the tray
    // slides/fades in on mount instead of teleporting into the list, so
    // "Añadir a comparar" reads as the item actually arriving here.
    // React unmounts a removed row immediately (no exit animation without a
    // library like framer-motion's AnimatePresence, out of scope for a
    // polish-only sprint) — this is entrance-only, which is still the more
    // noticeable half of the interaction.
    <div className="flex items-center gap-2 px-3 py-2 animate-in fade-in-0 slide-in-from-right-2 duration-200 motion-reduce:animate-none">
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-semibold leading-snug truncate" style={{ color: "var(--color-heading-navy)" }}>
          {exp.title}
        </p>
        <p className="text-[10px] leading-snug truncate" style={{ color: "oklch(0.545 0.020 60 / 0.50)" }}>
          {isProvider ? "Proveedor" : "Local"}{neighborhood ? ` · ${neighborhood}` : ""}
        </p>
      </div>
      {/* 44x44 minimum touch target (task 6) */}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Quitar "${exp.title}" de la comparación`}
        className={`shrink-0 flex items-center justify-center rounded-full transition-all duration-150 hover:opacity-70 active:scale-90 motion-reduce:transition-none motion-reduce:active:scale-100 ${FOCUS_RING}`}
        style={{ width: "44px", height: "44px", color: "oklch(0.545 0.020 60 / 0.55)", background: "oklch(0.545 0.020 60 / 0.06)" }}
      >
        <X size={14} aria-hidden />
      </button>
    </div>
  );
}
